import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ChatCircleDots } from "@phosphor-icons/react";

export default function Conversations() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/connexion"); return; }
    (async () => {
      try { const { data } = await api.get("/conversations"); setItems(data); }
      finally { setFetching(false); }
    })();
  }, [user, loading, navigate]);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="font-display text-3xl font-black text-stone-800" data-testid="conversations-title">Messages</h1>
        <p className="text-stone-600 mt-2">Toutes vos conversations autour d'un doudou — que vous soyez la famille qui l'a perdu ou la personne qui l'a trouvé.</p>

        {fetching ? <p className="mt-8 text-stone-500">Chargement...</p> :
        items.length === 0 ? (
          <div className="mt-8 bg-white rounded-3xl border border-rose-100 p-12 text-center">
            <ChatCircleDots size={56} weight="duotone" className="mx-auto text-rose-300" />
            <p className="mt-4 text-stone-500">Aucune conversation pour l'instant.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-3">
            {items.map(c => {
              const isFinderRole = c.role === "finder";
              const counterpartName = isFinderRole ? c.owner_first_name : c.finder_first_name;
              const doudouLabel = c.doudou_name || `doudou #${(c.doudou_id || "").slice(-6)}`;
              return (
                <Link key={c.conversation_id} to={`/conversations/${c.conversation_id}`} className={`block bg-white rounded-2xl border p-5 hover:shadow-md transition ${c.has_unread ? "border-orange-300 shadow-sm" : "border-rose-100"}`} data-testid={`conv-${c.conversation_id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-display font-bold text-stone-800 truncate">{counterpartName}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isFinderRole ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"}`}>
                          {isFinderRole ? "J'ai trouvé" : "Ma famille"}
                        </span>
                        {c.has_unread && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider animate-pulse" data-testid={`conv-unread-${c.conversation_id}`}>
                            Nouveau
                          </span>
                        )}
                      </div>
                      <p className={`text-sm truncate ${c.has_unread ? "text-stone-800 font-bold" : "text-stone-500"}`}>À propos de {doudouLabel}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <p className="text-xs text-stone-400">{new Date(c.last_message_at).toLocaleString("fr-FR")}</p>
                      {c.has_unread && <span className="w-2.5 h-2.5 rounded-full bg-orange-500" />}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
